'use client';

import { c as _c } from "react/compiler-runtime";
import type { ChangeEvent } from 'react';
import { useEffect, useReducer, useState } from 'react';
import { bestKey } from '../funGames';
import { useGameTracking } from '../useGameTracking';
import { useLocalBest } from '../useLocalBest';
import { moonPhaseFor, PET, SACRIFICE_COUNT, WORM } from './passwordData';
import { contextFor, FIRE, initialState, makeReducer, sacrificeOptions } from './passwordGameState';
import PasswordRuleList from './PasswordRuleList';
import { countPassing, PASSWORD_RULES, triggersFor } from './passwordRules';
const BEST = bestKey('password');

/** How often the fire advances. Slow enough to be survivable, fast enough to hurt. */
const FIRE_INTERVAL_MS = 3000;
const PasswordGame = () => {
  const $ = _c(63);
  const [today] = useState(_temp);
  const extras = {
    moonEmoji: moonPhaseFor(today),
    dayOfMonth: today.getDate()
  };
  const [state, dispatch] = useReducer(makeReducer(extras), initialState);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = [];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const [picked, setPicked] = useState(t0);
  const best = useLocalBest(BEST);
  const {
    record
  } = useGameTracking();
  const ctx = contextFor(state, extras);
  const visible = PASSWORD_RULES.slice(0, state.revealed);
  const passing = visible.filter(rule => rule.test(ctx)).length;
  let needsSacrifice;
  let t1;
  if ($[1] !== state.revealed || $[2] !== state.sacrificed) {
    const triggers = triggersFor(state.revealed);
    needsSacrifice = triggers.includes("sacrifice") && state.sacrificed.length === 0;
    t1 = triggers.includes("fire");
    $[1] = state.revealed;
    $[2] = state.sacrificed;
    $[3] = needsSacrifice;
    $[4] = t1;
  } else {
    needsSacrifice = $[3];
    t1 = $[4];
  }
  const fireActive = t1;
  const playing = state.status === "playing";
  const onFire = state.password.includes(FIRE);
  let t2;
  if ($[5] !== dispatch || $[6] !== fireActive || $[7] !== playing) {
    t2 = () => {
      if (!playing || !fireActive) {
        return;
      }
      const id = setInterval(() => dispatch({
        type: "tick"
      }), FIRE_INTERVAL_MS);
      return () => clearInterval(id);
    };
    $[5] = dispatch;
    $[6] = fireActive;
    $[7] = playing;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  let t3;
  if ($[9] !== fireActive || $[10] !== playing) {
    t3 = [playing, fireActive];
    $[9] = fireActive;
    $[10] = playing;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  useEffect(t2, t3);
  let t4;
  if ($[12] !== ctx || $[13] !== dispatch || $[14] !== record) {
    t4 = event => {
      const value = event.target.value;
      dispatch({
        type: "type",
        value
      });
      record("password", countPassing({
        ...ctx,
        password: value
      }));
    };
    $[12] = ctx;
    $[13] = dispatch;
    $[14] = record;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  const change = t4;
  let t5;
  if ($[16] !== dispatch || $[17] !== picked || $[18] !== setPicked) {
    t5 = letter => {
      if (picked.includes(letter)) {
        setPicked(picked.filter(entry => entry !== letter));
        return;
      }
      const next = [...picked, letter];
      setPicked(next);
      if (next.length === SACRIFICE_COUNT) {
        dispatch({
          type: "sacrifice",
          letters: next
        });
      }
    };
    $[16] = dispatch;
    $[17] = picked;
    $[18] = setPicked;
    $[19] = t5;
  } else {
    t5 = $[19];
  }
  const choose = t5;
  let t6;
  if ($[20] !== dispatch || $[21] !== setPicked) {
    t6 = () => {
      setPicked([]);
      dispatch({
        type: "restart"
      });
    };
    $[20] = dispatch;
    $[21] = setPicked;
    $[22] = t6;
  } else {
    t6 = $[22];
  }
  const restart = t6;
  let t7;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <label htmlFor="fun-password" className="mb-2 block text-sm font-emphasis text-textSecondary">Please choose a password</label>;
    $[23] = t7;
  } else {
    t7 = $[23];
  }
  const t8 = state.status === "lost";
  const t9 = `w-full resize-none rounded-xl border bg-background-secondary p-4 font-mono text-lg text-textPrimary outline-none transition-colors disabled:opacity-60 ${onFire ? "border-orange-500 focus:border-orange-400" : "border-ds-border-standard focus:border-ds-border-strong"}`;
  let t10;
  if ($[24] !== change || $[25] !== state.password || $[26] !== t8 || $[27] !== t9) {
    t10 = <textarea id="fun-password" value={state.password} onChange={change} rows={3} spellCheck={false} autoComplete="off" autoCapitalize="off" autoCorrect="off" disabled={t8} aria-describedby="fun-password-rules" className={t9} />;
    $[24] = change;
    $[25] = state.password;
    $[26] = t8;
    $[27] = t9;
    $[28] = t10;
  } else {
    t10 = $[28];
  }
  let t11;
  if ($[29] !== state.password) {
    t11 = [...state.password];
    $[29] = state.password;
    $[30] = t11;
  } else {
    t11 = $[30];
  }
  let t12;
  if ($[31] !== t11.length) {
    t12 = <span>{t11.length} characters</span>;
    $[31] = t11.length;
    $[32] = t12;
  } else {
    t12 = $[32];
  }
  let t13;
  if ($[33] !== passing) {
    t13 = <span>{passing}/{PASSWORD_RULES.length} rules</span>;
    $[33] = passing;
    $[34] = t13;
  } else {
    t13 = $[34];
  }
  let t14;
  if ($[35] !== t12 || $[36] !== t13) {
    t14 = <div className="mt-2 flex items-center justify-between text-sm text-textGray">{t12}{t13}</div>;
    $[35] = t12;
    $[36] = t13;
    $[37] = t14;
  } else {
    t14 = $[37];
  }
  const t15 = playing && <div className="mt-3 flex flex-wrap items-center gap-2"><span className="text-xs uppercase tracking-wide text-textGray">Insert</span>{[extras.moonEmoji, WORM, PET].map(emoji => <button key={emoji} type="button" onClick={() => dispatch({
      type: "insert",
      text: emoji
    })} aria-label={`Insert ${emoji}`} className="rounded-lg border border-ds-border-standard px-3 py-1.5 text-lg transition-colors hover:bg-background-light">{emoji}</button>)}</div>;
  let t16;
  if ($[38] !== choose || $[39] !== needsSacrifice || $[40] !== picked) {
    t16 = needsSacrifice && <div className="mt-4 rounded-xl border border-ds-border-standard bg-background-secondary p-4"><p className="mb-3 text-sm text-textPrimary">Choose two letters to sacrifice. They can never appear in your password again.</p><div className="flex flex-wrap gap-2">{sacrificeOptions().map(letter_0 => {
          const chosen = picked.includes(letter_0);
          return <button key={letter_0} type="button" aria-pressed={chosen} onClick={() => choose(letter_0)} className={`h-10 w-10 rounded-lg border font-mono text-lg uppercase transition-colors ${chosen ? "border-destructive bg-destructive/20 text-textPrimary" : "border-ds-border-standard text-textPrimary hover:bg-background-light"}`}>{letter_0}</button>;
        })}</div><p className="mt-3 text-xs text-textGray">{picked.length}/2 chosen</p></div>;
    $[38] = choose;
    $[39] = needsSacrifice;
    $[40] = picked;
    $[41] = t16;
  } else {
    t16 = $[41];
  }
  let t17;
  if ($[42] !== restart || $[43] !== state.lossReason || $[44] !== state.status) {
    t17 = state.status === "lost" && <div className="mt-6 rounded-xl border border-destructive/50 bg-background-secondary p-5 text-center"><p className="text-2xl font-bold text-textPrimary">💀 Game over</p><p className="mt-1 text-textSecondary">{state.lossReason}</p><button type="button" onClick={restart} className="mt-4 rounded-full border border-ds-border-standard px-5 py-2 text-sm font-emphasis text-textPrimary transition-colors hover:bg-background-light">Start over</button></div>;
    $[42] = restart;
    $[43] = state.lossReason;
    $[44] = state.status;
    $[45] = t17;
  } else {
    t17 = $[45];
  }
  let t18;
  if ($[46] !== restart || $[47] !== state.status) {
    t18 = state.status === "won" && <div className="mt-6 rounded-xl border border-ds-border-standard bg-background-secondary p-5 text-center"><p className="text-2xl font-bold text-textPrimary">🎉 All {PASSWORD_RULES.length} rules</p><p className="mt-1 text-textSecondary">Now try to remember it tomorrow.</p><button type="button" onClick={restart} className="mt-4 rounded-full border border-ds-border-standard px-5 py-2 text-sm font-emphasis text-textPrimary transition-colors hover:bg-background-light">Start over</button></div>;
    $[46] = restart;
    $[47] = state.status;
    $[48] = t18;
  } else {
    t18 = $[48];
  }
  let t19;
  if ($[49] !== ctx || $[50] !== visible) {
    t19 = <PasswordRuleList rules={visible} ctx={ctx} />;
    $[49] = ctx;
    $[50] = visible;
    $[51] = t19;
  } else {
    t19 = $[51];
  }
  let t20;
  if ($[52] !== best) {
    t20 = best !== null && <p className="mt-6 text-sm text-textGray">Personal best: {best}/{PASSWORD_RULES.length} rules cleared</p>;
    $[52] = best;
    $[53] = t20;
  } else {
    t20 = $[53];
  }
  let t21;
  if ($[54] !== t10 || $[55] !== t14 || $[56] !== t15 || $[57] !== t16 || $[58] !== t17 || $[59] !== t18 || $[60] !== t19 || $[61] !== t20) {
    t21 = <div>{t7}{t10}{t14}{t15}{t16}{t17}{t18}{t19}{t20}</div>;
    $[54] = t10;
    $[55] = t14;
    $[56] = t15;
    $[57] = t16;
    $[58] = t17;
    $[59] = t18;
    $[60] = t19;
    $[61] = t20;
    $[62] = t21;
  } else {
    t21 = $[62];
  }
  return t21;
};
export default PasswordGame;
function _temp() {
  return new Date();
}